import {
  Truck,
  FileText,
  CheckCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/classnames';
import type { MatchedLineItem } from '@/features/receiving/types/ai-analysis';
import type {
  Delivery, DeliveryLine 
} from '@/features/receiving/types';

function getLineItemClass(status: string, hasMatch: boolean): string {
  if (status === 'received') return 'bg-green-50 border-green-200';
  if (hasMatch) return 'bg-blue-50 border-blue-200';
  return 'bg-white';
}

interface LineItemRowProps {
  readonly line: DeliveryLine;
  readonly match: MatchedLineItem | undefined;
}

function LineItemRow({
  line, match 
}: LineItemRowProps) {
  const hasReceivedQty = line.receivedQuantity !== undefined;
  return (
    <div className={cn(
      'flex items-center justify-between p-3 border rounded-lg',
      getLineItemClass(line.status, Boolean(match))
    )}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium truncate">{line.itemName}</p>
          {line.status === 'received' && (
            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
          )}
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span>SKU: {line.sku}</span>
          <span>Ordered: {line.orderedQuantity} {line.unit}</span>
        </div>
        {match && (
          <div className="mt-1 text-sm text-blue-700">
            AI suggests: {match.suggestedQuantity} {line.unit}
            {match.suggestedLotNumber && ` • Lot: ${match.suggestedLotNumber}`}
          </div>
        )}
      </div>
      <div className="text-right">
        {hasReceivedQty && (
          <p className="font-medium text-green-700">
            {line.receivedQuantity} {line.unit}
          </p>
        )}
        {!hasReceivedQty && match && (
          <Button size="sm" variant="outline">Apply</Button>
        )}
        {!hasReceivedQty && !match && (
          <span className="text-sm text-gray-400">Pending</span>
        )}
      </div>
    </div>
  );
}

interface ReviewTabProps {
  readonly delivery: Delivery;
  readonly matchedLines: MatchedLineItem[];
  readonly receivingProgress: {
    received: number;
    total: number;
  };
  readonly onScanInvoice: () => void;
}

export function ReviewTab({
  delivery,
  matchedLines,
  receivingProgress,
  onScanInvoice,
}: ReviewTabProps) {
  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center gap-3 mb-4">
          <Truck className="w-5 h-5 text-gray-400" />
          <div>
            <h3 className="font-medium">Delivery Summary</h3>
            <p className="text-sm text-gray-500">
              {delivery.totalLines} items • Expected {delivery.scheduledDate}
            </p>
          </div>
        </div>
        {matchedLines.length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-blue-800">
              AI matched {matchedLines.length} items from invoice
            </p>
          </div>
        )}
        <div className="space-y-2">
          {delivery.lines.map((line) => (
            <LineItemRow
              key={line.lineId}
              line={line}
              match={matchedLines.find((m) => m.deliveryLineId === line.lineId)}
            />
          ))}
        </div>
      </Card>
      <div className="flex gap-3">
        <Button variant="outline" className="flex-1" onClick={onScanInvoice}>
          <FileText className="w-4 h-4 mr-2" />Scan Invoice
        </Button>
        <Button className="flex-1" disabled={receivingProgress.received === 0}>
          <CheckCircle className="w-4 h-4 mr-2" />Complete Receiving
        </Button>
      </div>
    </div>
  );
}
